/**
 * Maaş ödeme kayıtları.
 * Bir personel için ay başına BİRDEN FAZLA aktif ödeme olabilir
 * (örn ön ödeme + sonradan puantaj farkı için ek ödeme).
 */

import {
  collection, doc, getDocs, setDoc, updateDoc, query, where,
} from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { nowServer } from '@/lib/firestore'
import type { FsTimestamp } from '@/types'

const ODEMELER = 'maasOdemeleri'

export type OdemeTipi = 'nakit' | 'iban' | 'karma'

export interface MaasOdemesi {
  id: string
  personelId: string
  donem: string
  netTutar: number  // bu ödemenin tutarı (kısmi olabilir)
  odemeTipi: OdemeTipi
  nakitTutar?: number
  ibanTutar?: number
  notlar?: string
  odemeTarihi: FsTimestamp
  odeyenId: string
  iptal: boolean
  iptalTarihi?: FsTimestamp
  iptalEdenId?: string
}

export interface OdemeKaydetData {
  personelId: string
  donem: string
  netTutar: number
  odemeTipi: OdemeTipi
  nakitTutar?: number
  ibanTutar?: number
  notlar?: string
}

function yeniId(): string {
  const db = getFirestoreDb()
  return doc(collection(db, ODEMELER)).id
}

export async function odemeKaydet(
  data: OdemeKaydetData,
  odeyenId: string
): Promise<string> {
  if (data.netTutar <= 0) throw new Error('Ödeme tutarı pozitif olmalı.')

  if (data.odemeTipi === 'karma') {
    const toplam = (data.nakitTutar ?? 0) + (data.ibanTutar ?? 0)
    if (Math.abs(toplam - data.netTutar) > 0.01) {
      throw new Error(`Nakit + IBAN toplamı tutara eşit olmalı (${data.netTutar.toFixed(2)} ₺).`)
    }
  }

  const db = getFirestoreDb()
  const id = yeniId()

  const docData: Record<string, unknown> = {
    personelId: data.personelId,
    donem: data.donem,
    netTutar: data.netTutar,
    odemeTipi: data.odemeTipi,
    odemeTarihi: nowServer(),
    odeyenId,
    iptal: false,
  }
  if (data.nakitTutar !== undefined) docData.nakitTutar = data.nakitTutar
  if (data.ibanTutar !== undefined) docData.ibanTutar = data.ibanTutar
  if (data.notlar?.trim()) docData.notlar = data.notlar.trim()

  await setDoc(doc(db, ODEMELER, id), docData)
  return id
}

export async function odemeIptalEt(
  odemeId: string,
  iptalEdenId: string
): Promise<void> {
  const db = getFirestoreDb()
  await updateDoc(doc(db, ODEMELER, odemeId), {
    iptal: true,
    iptalTarihi: nowServer(),
    iptalEdenId,
  })
}

export async function donemOdemelerini(donem: string): Promise<MaasOdemesi[]> {
  const db = getFirestoreDb()
  const q = query(collection(db, ODEMELER), where('donem', '==', donem))
  const snap = await getDocs(q)
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as MaasOdemesi)
}

export async function personelOdemelerini(
  personelId: string,
  donem: string
): Promise<MaasOdemesi[]> {
  const db = getFirestoreDb()
  const q = query(
    collection(db, ODEMELER),
    where('personelId', '==', personelId)
  )
  const snap = await getDocs(q)
  const liste = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as MaasOdemesi)
  return liste
    .filter((o) => o.donem === donem && !o.iptal)
    .sort((a, b) => a.odemeTarihi.toMillis() - b.odemeTarihi.toMillis())
}
